import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId, loginToast } from "@/lib/ui";
import { EASE } from "./Reveal";

const LINKS = [
  { label: "HOME", id: "home" },
  { label: "P.E.E. OS", id: "pee-os" },
  { label: "HOW IT WORKS", id: "how-it-works" },
  { label: "TRAINING", id: "training" },
  { label: "COMMUNITY", id: "community" },
  { label: "RESULTS", id: "results" },
  { label: "PRICING", id: "pricing" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <header
        data-testid="header-navigation"
        className="fixed inset-x-0 top-0 z-50 border-b border-line/70 bg-ink/75 backdrop-blur-xl"
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:h-[72px] lg:px-8">
          <button
            data-testid="header-logo"
            onClick={() => go("home")}
            className="flex items-center gap-3"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-md border border-neon/40 bg-neon/10">
              <span className="pulse-dot h-2 w-2 rounded-full bg-neon" />
            </span>
            <span className="text-left leading-none">
              <span className="block font-display text-lg font-extrabold tracking-tight text-white">
                P.E.E. OS
              </span>
              <span className="mt-1 block font-mono text-[9px] uppercase tracking-[0.3em] text-dim">
                Execution Traders
              </span>
            </span>
          </button>

          <nav className="hidden items-center gap-7 xl:flex">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="font-mono text-[11px] uppercase tracking-[0.18em] text-mist transition-colors duration-300 hover:text-neon"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="hidden items-center gap-3 xl:flex">
            <button
              data-testid="header-login-button"
              onClick={loginToast}
              className="rounded-md px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:text-neon"
            >
              Login
            </button>
            <button
              data-testid="header-join-button"
              onClick={() => go("pricing")}
              className="rounded-md bg-neon px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-ink transition-shadow duration-300 hover:shadow-[0_0_24px_rgba(0,255,102,0.45)]"
            >
              Join Execution Traders
            </button>
          </div>

          <button
            data-testid="header-mobile-menu-trigger"
            onClick={() => setOpen(!open)}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-line text-white xl:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-ink/97 px-8 backdrop-blur-2xl xl:hidden"
          >
            <nav className="space-y-2">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`mobile-nav-link-${l.id}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.5, ease: EASE }}
                  onClick={() => go(l.id)}
                  className="block w-full border-b border-line/60 py-4 text-left font-display text-2xl font-bold uppercase tracking-tight text-white transition-colors hover:text-neon"
                >
                  <span className="mr-4 font-mono text-xs text-neon/70">
                    0{i + 1}
                  </span>
                  {l.label}
                </motion.button>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5, ease: EASE }}
              className="mt-10 flex flex-col gap-3"
            >
              <button
                data-testid="mobile-join-button"
                onClick={() => go("pricing")}
                className="rounded-md bg-neon px-6 py-4 font-display text-sm font-bold uppercase tracking-wider text-ink"
              >
                Join Execution Traders
              </button>
              <button
                data-testid="mobile-login-button"
                onClick={() => {
                  setOpen(false);
                  loginToast();
                }}
                className="rounded-md border border-line px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white"
              >
                Login
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
